// Copyright (c) 2026. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted
import { CurrencyInvoiceSummary, InvoiceSummary } from '@/billing/api/types';
import { PerCurrencySections } from '@/billing/components/currency-section-accordion';
import { PrimaryMetric, SummaryCard } from '@/components/summary-card';
import { formatCurrencyValue } from '@/currency/utilities/format';
import { pluralize } from '@/utilities/pluralize';
import { Box, Grid } from '@mui/material';
import { Decimal } from 'decimal.js';

interface InvoiceSummaryCardsProps {
  // `null` signals "data still loading". Tiles render skeletons in that
  // state; they must never display fabricated zero values while the fetch
  // is in flight, since $0 is itself a meaningful business state.
  summary: InvoiceSummary | null;
}

// Every non-void invoice falls into exactly one of these four buckets:
//   * Draft         — not yet issued (pre-AR queue)
//   * Current       — issued, not yet due, balance remaining
//   * Overdue       — issued, past due, balance remaining
//   * Paid (30d)    — paid within the trailing 30 days
// Current and Overdue are mutually exclusive along the due-date axis —
// labeling the first "Open" would overlap with overdue colloquially.
// Aging-bucket slicing lives on the separate A/R aging report, not here.
// The breakdown is bucketed per currency; single-currency tenants (the
// common case) see exactly one group.
export function InvoiceSummaryCards(props: InvoiceSummaryCardsProps) {
  const { summary } = props;

  if (summary === null) {
    return (
      <Box sx={{ mb: 2 }}>
        <CurrencyTileRow bucket={null} />
      </Box>
    );
  }

  return (
    <Box sx={{ mb: 2 }}>
      <PerCurrencySections
        buckets={summary.byCurrency}
        renderBucket={(bucket) => <CurrencyTileRow bucket={bucket} />}
      />
    </Box>
  );
}

interface CurrencyTileRowProps {
  // `null` signals the loading state; every tile renders a skeleton.
  bucket: CurrencyInvoiceSummary | null;
}

function CurrencyTileRow(props: CurrencyTileRowProps) {
  const { bucket } = props;
  const isLoading = bucket === null;
  const overdueAmount = bucket ? Number(bucket.overdueAmount) || 0 : 0;
  const overdueColor = overdueAmount > 0 ? 'error.main' : 'text.secondary';

  function tileValue(amount: string): string {
    return bucket
      ? formatCurrencyValue({
          amount: new Decimal(amount),
          isoCode: bucket.isoCode,
        })
      : '';
  }

  return (
    <Grid container spacing={3}>
      <Grid item xs={12} sm={6} md={3}>
        <SummaryCard>
          <PrimaryMetric
            label="Draft"
            value={bucket ? tileValue(bucket.draftAmount) : ''}
            color="text.secondary"
            isLoading={isLoading}
            caption={
              bucket
                ? pluralize(bucket.draftCount, { singular: 'invoice' })
                : undefined
            }
          />
        </SummaryCard>
      </Grid>
      <Grid item xs={12} sm={6} md={3}>
        <SummaryCard>
          <PrimaryMetric
            label="Current"
            value={bucket ? tileValue(bucket.currentAmount) : ''}
            color="warning.dark"
            isLoading={isLoading}
            caption={
              bucket
                ? pluralize(bucket.currentCount, { singular: 'invoice' })
                : undefined
            }
          />
        </SummaryCard>
      </Grid>
      <Grid item xs={12} sm={6} md={3}>
        <SummaryCard>
          <PrimaryMetric
            label="Overdue"
            value={bucket ? tileValue(bucket.overdueAmount) : ''}
            color={overdueColor}
            isLoading={isLoading}
            caption={
              bucket
                ? pluralize(bucket.overdueCount, { singular: 'invoice' })
                : undefined
            }
          />
        </SummaryCard>
      </Grid>
      <Grid item xs={12} sm={6} md={3}>
        <SummaryCard>
          <PrimaryMetric
            label="Paid (last 30 days)"
            value={bucket ? tileValue(bucket.paidRecentAmount) : ''}
            color="success.dark"
            isLoading={isLoading}
            caption={
              bucket
                ? pluralize(bucket.paidRecentCount, { singular: 'invoice' })
                : undefined
            }
          />
        </SummaryCard>
      </Grid>
    </Grid>
  );
}
